import React, { ChangeEvent, useEffect, useState, FocusEvent } from 'react';
import { UiSelect, UiCheckbox, UiInput } from '@frontend/znanie-uikit';
import { useApolloClient } from '@apollo/client';
import cn from 'classnames';

import CITY_QUERY from 'graphql/address/queries/getCity.graphql';

import {
  GetCityQuery,
  GetCityQueryVariables,
  AddressInput,
} from 'graphql/types';

import css from './RegionsSelector.module.scss';

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
  instanceId?: string;
  errorLabel?: string;
  defaultOption?: AddressInput | null;
  customLabel?: string;
  customPlaceholder?: string;
  customNoOptionsLabel?: string;
  errorLabelCustomAddress?: string;
  defaultCustomAddress?: string;
  viewCustomAddress?: boolean;
  disabled?: boolean;

  onRegionSelect?(region: AddressInput): void;
  onChangeCustomAddress?(value: string): void;
  onBlurSelect?(e: React.FocusEvent): void;
  onBlurCustomAddress?(value: string): void;
  onChangeCustomAddressCheckbox?(checked: boolean): void;
}

export default function RegionsSelector(props: IProps) {
  const {
    tabIndex,
    defaultOption,
    errorLabel,
    onRegionSelect,
    onBlurSelect,
    customLabel,
    customPlaceholder,
    defaultCustomAddress,
    onChangeCustomAddress,
    onChangeCustomAddressCheckbox,
    onBlurCustomAddress,
    errorLabelCustomAddress,
    customNoOptionsLabel,
    viewCustomAddress,
    disabled,
  } = props;
  const [selectedOption, setSelectedOption] = useState<any>();
  const [checkedCustomAddress, setCheckedCustomAddress] =
    useState<boolean>(false);
  const apolloClient = useApolloClient();
  let loadTimeoutId: NodeJS.Timeout;

  const loadRegions = async (inputValue: string, callback: Function) => {
    try {
      // useLazyQuery не возвращает сразу результат, а он нужен после выполнения для передачи в callback
      const { data } = await apolloClient.query<
        GetCityQuery,
        GetCityQueryVariables
      >({
        query: CITY_QUERY,
        variables: { term: inputValue },
      });
      const results = data?.suggestCity;

      callback(results);
    } catch (err) {
      console.log(err);
      callback([]);
    }
  };

  const handleSelectRegion = (option: any) => {
    setSelectedOption(option);
    if (onRegionSelect) {
      const opt = { ...option };

      delete opt.__typename;
      onRegionSelect(opt);
    }
  };

  const onLoadRegions = (inputValue: string, callback: Function) => {
    if (loadTimeoutId) {
      clearTimeout(loadTimeoutId);
    }
    loadTimeoutId = setTimeout(async () => {
      await loadRegions(inputValue || defaultOption?.value || '', callback);
    }, 500);
  };

  const changeCustomAddress = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>): void => {
    onChangeCustomAddress && onChangeCustomAddress(value);
  };

  const blurCustomAddress = ({
    target: { value },
  }: FocusEvent<HTMLInputElement>): void => {
    onBlurCustomAddress && onBlurCustomAddress(value);
  };

  const onChangeCheckbox = ({
    target: { checked },
  }: ChangeEvent<HTMLInputElement>): void => {
    setCheckedCustomAddress(checked);
    onChangeCustomAddressCheckbox && onChangeCustomAddressCheckbox(checked);
  };

  useEffect(() => {
    if (defaultOption) {
      setSelectedOption(defaultOption);
    }
  }, [defaultOption]);

  useEffect(() => {
    if (viewCustomAddress && defaultCustomAddress) {
      setCheckedCustomAddress(true);
    }
  }, [defaultCustomAddress]);

  return (
    <>
      <UiSelect
        id={props?.id || ''}
        instanceId={props?.instanceId || ''}
        tabIndex={Number(tabIndex)}
        label={customLabel || 'Регион проживания'}
        errorLabel={errorLabel}
        async
        value={selectedOption}
        cacheOptions
        defaultOptions={!!defaultOption}
        loadOptions={onLoadRegions}
        onChange={handleSelectRegion}
        onBlur={(e) => onBlurSelect && onBlurSelect(e)}
        getOptionLabel={(option: any) => option.value}
        getOptionValue={(option: any) => option.kladrId}
        placeholder={customPlaceholder || 'Укажите населенный пункт'}
        noOptionsMessage={() =>
          customNoOptionsLabel || 'Начните вводить название населенного пункта'
        }
        loadingMessage={() => 'Идет поиск...'}
        isDisabled={disabled || checkedCustomAddress}
      />
      {viewCustomAddress && (
        <>
          <UiCheckbox
            checked={checkedCustomAddress}
            onChange={onChangeCheckbox}
            label="Ввести адрес вручную"
          />
          <UiInput
            defaultValue={defaultCustomAddress}
            wrapperClass={cn(
              css.customAddress,
              checkedCustomAddress && css.show
            )}
            fluid
            placeholder="Регион, населенный пункт"
            maxLength={255}
            onChange={changeCustomAddress}
            onBlur={blurCustomAddress}
            errorLabel={errorLabelCustomAddress}
          />
        </>
      )}
    </>
  );
}
